import * as React from 'react';
import { toast } from 'sonner';
import { Callout } from '@/components/patterns/callout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/field';
import { dataSource } from '@/data';
import { mensagemDoErro } from '@/data/http';
import type { EdicaoIncidente, IncidenteDetalhe, SeveridadeIncidente } from '@/data/types';
import { SEVERIDADES } from '@/data/types';
import { emitir } from '@/lib/eventos';
import { ROTULO_SEVERIDADE, validarCorrigidoEm, validarLink, validarTitulo } from '@/lib/incidentes';
import { cn } from '@/lib/utils';
import { VersoesSeletor } from './VersoesSeletor';

/**
 * Registro e edição de incidente no mesmo formulário. No registro as versões
 * afetadas são obrigatórias (≥ 1) e podem vir pré-preenchidas com a versão de
 * onde o diálogo foi aberto; na edição elas são gerenciadas no detalhe do
 * incidente (adicionar/remover), onde cada mudança vira evento próprio.
 *
 * Erros 404/409/422 da API aparecem inline com a mensagem real.
 */

interface Erros {
  titulo?: string;
  link?: string;
  corrigido_em?: string;
  versoes?: string;
}

export function IncidenteDialog({
  aberto,
  aoFechar,
  incidente,
  versoesIniciais = [],
  aoSalvar,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /** Presente = edição. */
  incidente?: IncidenteDetalhe | null;
  versoesIniciais?: string[];
  aoSalvar: (detalhe: IncidenteDetalhe) => void;
}) {
  const edicao = Boolean(incidente);
  const [titulo, setTitulo] = React.useState('');
  const [descricao, setDescricao] = React.useState('');
  const [severidade, setSeveridade] = React.useState<SeveridadeIncidente>('media');
  const [link, setLink] = React.useState('');
  const [corrigidoEm, setCorrigidoEm] = React.useState('');
  const [versoes, setVersoes] = React.useState<string[]>([]);
  const [erros, setErros] = React.useState<Erros>({});
  const [erroApi, setErroApi] = React.useState<string | null>(null);
  const [enviando, setEnviando] = React.useState(false);

  // Reinicia o formulário a cada abertura (e quando o incidente em edição muda).
  React.useEffect(() => {
    if (!aberto) return;
    setTitulo(incidente?.titulo ?? '');
    setDescricao(incidente?.descricao ?? '');
    setSeveridade(incidente?.severidade ?? 'media');
    setLink(incidente?.link ?? '');
    setCorrigidoEm(incidente?.corrigido_em ?? '');
    setVersoes(incidente ? [] : versoesIniciais);
    setErros({});
    setErroApi(null);
    // `versoesIniciais` é lido só na abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, incidente?.id]);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    const novos: Erros = {
      titulo: validarTitulo(titulo) ?? undefined,
      link: validarLink(link) ?? undefined,
      corrigido_em: validarCorrigidoEm(corrigidoEm) ?? undefined,
      versoes: !edicao && versoes.length === 0 ? 'Escolha pelo menos uma versão afetada.' : undefined,
    };
    setErros(novos);
    if (Object.values(novos).some(Boolean)) return;

    setEnviando(true);
    setErroApi(null);
    try {
      let detalhe: IncidenteDetalhe;
      if (incidente) {
        const mudancas: EdicaoIncidente = {};
        if (titulo.trim() !== incidente.titulo) mudancas.titulo = titulo.trim();
        if (descricao.trim() !== incidente.descricao) mudancas.descricao = descricao.trim();
        if (severidade !== incidente.severidade) mudancas.severidade = severidade;
        if ((link.trim() || null) !== incidente.link) mudancas.link = link.trim() || null;
        if ((corrigidoEm.trim() || null) !== incidente.corrigido_em) mudancas.corrigido_em = corrigidoEm.trim() || null;
        if (Object.keys(mudancas).length === 0) {
          setErroApi('Nada a alterar: os valores já são os atuais.');
          return;
        }
        detalhe = await dataSource.editarIncidente(incidente.id, mudancas);
        toast.success(`Incidente #${detalhe.id} atualizado.`);
      } else {
        detalhe = await dataSource.criarIncidente({
          titulo,
          descricao,
          severidade,
          link,
          corrigido_em: corrigidoEm,
          versoes,
        });
        toast.success(`Incidente #${detalhe.id} registrado.`);
      }
      emitir({ tipo: 'incidente', id: detalhe.id, versoes: detalhe.versoes.map((v) => v.versao) });
      aoSalvar(detalhe);
    } catch (causa) {
      setErroApi(mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa)));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(valor) => !valor && !enviando && aoFechar()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[min(40rem,calc(100vw-2rem))] overflow-y-auto">
        <form onSubmit={enviar} noValidate className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{edicao ? `Editar incidente #${incidente?.id}` : 'Registrar incidente'}</DialogTitle>
            <DialogDescription>
              {edicao
                ? 'As mudanças ficam na linha do tempo do incidente, com antes e depois.'
                : 'Um bug conhecido que afeta uma ou mais versões. Quem consulta o feed vê o aviso, e quem homologa é avisado.'}
            </DialogDescription>
          </DialogHeader>

          <Field id="incidente-titulo" label="Título" obrigatorio erro={erros.titulo}>
            {(props) => (
              <Input
                {...props}
                autoFocus
                value={titulo}
                maxLength={220}
                onChange={(evento) => setTitulo(evento.target.value)}
                placeholder="Ex.: Erro ao emitir NF-e com ICMS-ST"
              />
            )}
          </Field>

          <Field id="incidente-descricao" label="Descrição" apoio="Sintomas, como reproduzir e workaround, se houver.">
            {(props) => (
              <Textarea {...props} value={descricao} onChange={(evento) => setDescricao(evento.target.value)} />
            )}
          </Field>

          <div className="flex flex-col gap-1.5" role="radiogroup" aria-labelledby="incidente-severidade-rotulo">
            <span id="incidente-severidade-rotulo" className="text-label text-on-surface">
              Severidade<span className="ml-1 text-danger">*</span>
            </span>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
              {SEVERIDADES.slice()
                .reverse()
                .map((valor) => (
                  <button
                    key={valor}
                    type="button"
                    role="radio"
                    aria-checked={severidade === valor}
                    onClick={() => setSeveridade(valor)}
                    className={cn(
                      'h-10 rounded-input border px-3 text-label transition-colors duration-fast ease-standard',
                      severidade === valor
                        ? 'border-primary-border bg-primary-wash text-primary'
                        : 'border-border-strong bg-bg-surface font-normal text-on-surface hover:border-primary',
                    )}
                  >
                    {ROTULO_SEVERIDADE[valor]}
                  </button>
                ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="incidente-link" label="Link do ticket" erro={erros.link} apoio="Opcional. Só http:// ou https://.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="url"
                  value={link}
                  onChange={(evento) => setLink(evento.target.value)}
                  placeholder="https://jira.nasajon.com.br/browse/ABC-1"
                />
              )}
            </Field>
            <Field id="incidente-corrigido" label="Corrigido em" erro={erros.corrigido_em} apoio="Opcional. Versão que traz a correção.">
              {(props) => (
                <Input
                  {...props}
                  value={corrigidoEm}
                  onChange={(evento) => setCorrigidoEm(evento.target.value)}
                  placeholder="2.2602.80.0"
                  className="font-mono"
                />
              )}
            </Field>
          </div>

          {edicao ? (
            <p className="rounded-input border border-border bg-bg-subtle px-3 py-2 text-cell text-on-surface-muted">
              As versões afetadas se gerenciam no detalhe do incidente (adicionar e remover).
            </p>
          ) : (
            <VersoesSeletor
              id="incidente-versoes"
              obrigatorio
              valores={versoes}
              aoMudar={setVersoes}
              erro={erros.versoes}
            />
          )}

          {erroApi ? <Callout tom="erro">{erroApi}</Callout> : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={enviando}>
              {edicao ? 'Salvar alterações' : 'Registrar incidente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
